import type { ImagePickerAsset } from 'expo-image-picker';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
  type StyleProp,
  type NativeSyntheticEvent,
  Platform,
  type TextInputSelectionChangeEventData,
  type TextInputProps,
  type TextStyle,
  type ViewStyle,
} from 'react-native';

import { IdentityHeader } from '@/src/components/IdentityHeader';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import {
  imageAssetFromPasteEvent,
  preventDefaultPaste,
} from '@/src/features/media/clipboard-media';

import {
  searchMentionProfiles,
  type MentionCandidate,
  type ProfileMention,
} from './post-service';
import { profileMentionsForText } from './profile-mention-helpers';
import { profileMentionLabel } from './profile-mention-utils';

export { profileMentionLabel };

type MentionScope =
  | { kind: 'post' }
  | {
      kind: 'comment';
      postId: string;
    }
  | {
      kind: 'vehicle_comment';
      vehicleId: string;
    };

export function activeProfileMentionToken(
  value: string,
  cursor: number,
  mentions: ProfileMention[] = [],
) {
  const prefix = value.slice(0, cursor);
  const match = /(^|\s)@([a-zA-Z0-9_ ]{0,40})$/.exec(prefix);

  if (!match) {
    return null;
  }

  const query = match[2].trim();
  const atOffset = match.index + match[1].length;
  const fragment = value.slice(atOffset, cursor);
  const selectedMention = mentions.some((mention) => {
    const label = mention.label.trim();

    if (!label || !fragment.startsWith(label)) {
      return false;
    }

    const nextCharacter = fragment.charAt(label.length);

    return nextCharacter === '' || /\s/.test(nextCharacter);
  });

  if (selectedMention) {
    return null;
  }

  return {
    atOffset,
    cursor,
    query,
  };
}

function inferredSelectionAfterTextChange({
  nextValue,
  previousSelection,
  previousValue,
}: {
  nextValue: string;
  previousSelection: { end: number; start: number };
  previousValue: string;
}) {
  if (previousSelection.start !== previousSelection.end) {
    return previousSelection;
  }

  const lengthDelta = nextValue.length - previousValue.length;

  if (lengthDelta === 0) {
    return previousSelection;
  }

  const cursor = Math.max(
    0,
    Math.min(nextValue.length, previousSelection.start + lengthDelta),
  );

  return { end: cursor, start: cursor };
}

function selectionFromEvent(
  event: NativeSyntheticEvent<TextInputSelectionChangeEventData>,
) {
  const selection = event.nativeEvent.selection;

  if (
    typeof selection?.start !== 'number' ||
    typeof selection?.end !== 'number'
  ) {
    return null;
  }

  return selection;
}

export function focusTextInputAt(
  ref: RefObject<TextInput | null>,
  cursor: number,
) {
  const input = ref.current as
    | (TextInput & {
        setSelectionRange?: (start: number, end: number) => void;
        setNativeProps?: (props: {
          selection?: { end: number; start: number };
        }) => void;
      })
    | null;

  if (!input) {
    return;
  }

  input.focus();

  if (typeof input.setSelectionRange === 'function') {
    input.setSelectionRange(cursor, cursor);
    return;
  }

  input.setNativeProps?.({
    selection: { end: cursor, start: cursor },
  });
}

export function visibleProfileMentions(
  mentions: ProfileMention[],
  value: string,
) {
  return profileMentionsForText(value, mentions) as ProfileMention[];
}

export function ProfileMentionBadges({
  mentions,
  style,
}: {
  mentions: ProfileMention[];
  style?: StyleProp<ViewStyle>;
}) {
  const visibleMentions = mentions.filter(
    (mention) => mention.uid.trim() && mention.label.trim(),
  );

  if (visibleMentions.length === 0) {
    return null;
  }

  return (
    <View style={[styles.mentionBadges, style]}>
      {visibleMentions.map((mention) => (
        <Text
          key={`${mention.uid}-${mention.label}`}
          numberOfLines={1}
          style={styles.mentionBadge}
        >
          {mention.label}
        </Text>
      ))}
    </View>
  );
}

export function ProfileMentionInput({
  error,
  label,
  mentions,
  multiline,
  onBlur,
  onChangeText,
  onMentionsChange,
  onPasteImageAsset,
  placeholder,
  scope,
  showLabel = true,
  style,
  textAlignVertical,
  value,
  autoCapitalize,
  maxLength,
  numberOfLines,
}: {
  autoCapitalize?: TextInputProps['autoCapitalize'];
  error?: string;
  label: string;
  maxLength?: number;
  mentions: ProfileMention[];
  multiline?: boolean;
  numberOfLines?: number;
  onBlur?: () => void;
  onChangeText: (value: string) => void;
  onMentionsChange: (mentions: ProfileMention[]) => void;
  onPasteImageAsset?: (asset: ImagePickerAsset) => void;
  placeholder?: string;
  scope: MentionScope;
  showLabel?: boolean;
  style?: TextStyle;
  textAlignVertical?: 'auto' | 'top' | 'bottom' | 'center';
  value: string;
}) {
  const [selection, setSelection] = useState({
    end: value.length,
    start: value.length,
  });
  const [candidates, setCandidates] = useState<MentionCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const latestRequestRef = useRef(0);
  const inputRef = useRef<TextInput | null>(null);
  const scopeKind = scope.kind;
  const postId = scope.kind === 'comment' ? scope.postId : undefined;
  const vehicleId =
    scope.kind === 'vehicle_comment' ? scope.vehicleId : undefined;
  const token = useMemo(
    () => activeProfileMentionToken(value, selection.start, mentions),
    [mentions, selection.start, value],
  );
  const tokenActive = Boolean(token);
  const activeMentions = visibleProfileMentions(mentions, value);

  useEffect(() => {
    if (selection.start <= value.length && selection.end <= value.length) {
      return;
    }

    setSelection({
      end: value.length,
      start: value.length,
    });
  }, [selection.end, selection.start, value.length]);

  useEffect(() => {
    const query = token?.query ?? '';

    if (!tokenActive) {
      latestRequestRef.current += 1;
      setCandidates([]);
      setLoading(false);
      return;
    }

    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setLoading(true);

    const timeout = setTimeout(() => {
      void searchMentionProfiles({
        postId,
        query,
        scope: scopeKind,
        vehicleId,
      })
        .then((profiles) => {
          if (latestRequestRef.current === requestId) {
            setCandidates(profiles);
          }
        })
        .catch(() => {
          if (latestRequestRef.current === requestId) {
            setCandidates([]);
          }
        })
        .finally(() => {
          if (latestRequestRef.current === requestId) {
            setLoading(false);
          }
        });
    }, 180);

    return () => clearTimeout(timeout);
  }, [postId, scopeKind, token?.query, tokenActive, vehicleId]);

  function updateText(nextValue: string) {
    setSelection((currentSelection) =>
      inferredSelectionAfterTextChange({
        nextValue,
        previousSelection: currentSelection,
        previousValue: value,
      }),
    );
    onChangeText(nextValue);
    onMentionsChange(visibleProfileMentions(mentions, nextValue));
  }

  async function handlePaste(event: unknown) {
    const asset = await imageAssetFromPasteEvent(event);

    if (!asset) {
      return;
    }

    preventDefaultPaste(event);
    onPasteImageAsset?.(asset);
  }

  function selectCandidate(candidate: MentionCandidate) {
    if (!token) {
      return;
    }

    const label = profileMentionLabel(candidate);
    const nextValue = `${value.slice(0, token.atOffset)}${label} ${value.slice(
      token.cursor,
    )}`;
    const existingMentions = mentions.filter(
      (mention) => mention.uid !== candidate.uid || mention.label !== label,
    );
    const nextCursor = token.atOffset + label.length + 1;

    onChangeText(nextValue);
    onMentionsChange([...existingMentions, { uid: candidate.uid, label }]);
    const nextSelection = { end: nextCursor, start: nextCursor };

    setSelection(nextSelection);
    latestRequestRef.current += 1;
    setCandidates([]);
    setLoading(false);
    setTimeout(() => {
      setSelection(nextSelection);
      focusTextInputAt(inputRef, nextCursor);
    }, 0);
    setTimeout(() => {
      setSelection(nextSelection);
      focusTextInputAt(inputRef, nextCursor);
    }, 80);
    setTimeout(() => {
      setSelection(nextSelection);
      focusTextInputAt(inputRef, nextCursor);
    }, 180);
  }

  return (
    <View style={styles.container}>
      <View style={styles.field}>
        {showLabel ? <Text style={styles.label}>{label}</Text> : null}
        <View style={[styles.inputFrame, error ? styles.inputError : null]}>
          <TextInput
            ref={inputRef}
            accessibilityLabel={label}
            autoCapitalize={autoCapitalize}
            cursorColor={colors.primary}
            maxLength={maxLength}
            multiline={multiline}
            numberOfLines={numberOfLines}
            onBlur={onBlur}
            onChangeText={updateText}
            onSelectionChange={(
              event: NativeSyntheticEvent<TextInputSelectionChangeEventData>,
            ) => {
              const nextSelection = selectionFromEvent(event);

              if (nextSelection) {
                setSelection(nextSelection);
              }
            }}
            placeholder={placeholder}
            placeholderTextColor={colors.mutedText}
            selectionColor={colors.primary}
            style={[
              styles.input,
              style,
              Platform.OS === 'web' ? styles.inputWeb : null,
            ]}
            textAlignVertical={textAlignVertical}
            value={value}
            {...(Platform.OS === 'web' && onPasteImageAsset
              ? ({ onPaste: handlePaste } as unknown as TextInputProps)
              : null)}
          />
          <ProfileMentionBadges
            mentions={activeMentions}
            style={styles.inputBadges}
          />
        </View>
        {error ? <Text style={styles.error}>{error}</Text> : null}
      </View>
      {loading ? (
        <View style={styles.statusRow}>
          <ActivityIndicator color={colors.primary} size="small" />
          <Text style={styles.statusText}>Finding people...</Text>
        </View>
      ) : null}
      {candidates.length > 0 ? (
        <View style={styles.menu}>
          {candidates.map((candidate) => (
            <Pressable
              key={candidate.uid}
              accessibilityRole="button"
              onPress={() => selectCandidate(candidate)}
              style={({ pressed }) => [
                styles.candidate,
                pressed ? styles.candidatePressed : null,
              ]}
            >
              <IdentityHeader
                avatarSize={36}
                containerStyle={styles.candidateIdentity}
                density="compact"
                founderBadge={candidate.founderBadge}
                fullName={candidate.fullName}
                interactive={false}
                photoURL={candidate.photoURL}
                uid={candidate.uid}
                username={candidate.username}
                verifiedBadge={candidate.verifiedBadge === true}
              />
            </Pressable>
          ))}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: spacing.sm,
  },
  field: {
    gap: spacing.xs,
  },
  label: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '700',
  },
  inputFrame: {
    backgroundColor: colors.inputBackground,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  inputError: {
    borderColor: colors.danger,
  },
  input: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    color: colors.text,
    fontSize: 16,
    lineHeight: 22,
    minHeight: 48,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  inputWeb: {
    outlineColor: 'transparent',
    outlineOffset: 0,
    outlineStyle: 'solid',
    outlineWidth: 0,
  },
  error: {
    color: colors.danger,
    fontSize: 13,
  },
  inputBadges: {
    paddingBottom: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  mentionBadge: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    color: colors.primary,
    fontSize: 12,
    fontWeight: '900',
    lineHeight: 15,
    maxWidth: '100%',
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
  },
  mentionBadges: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 3,
  },
  statusRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
  },
  statusText: {
    color: colors.mutedText,
    fontSize: 13,
    fontWeight: '700',
  },
  menu: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
  },
  candidate: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
    padding: spacing.sm,
  },
  candidatePressed: {
    backgroundColor: colors.surfacePressed,
  },
  candidateIdentity: {
    flex: 1,
  },
});
